import { Clear, Search } from "@mui/icons-material";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { PMButton } from "@pimo/pimo-components";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { Dayjs } from "dayjs";

import { getDateInputFormat } from "../../utils/format-date";
import {
  ControlledDateFormField,
  ControlledSelectField,
  FormFieldWrapper,
} from "../pimo-form";

export type LogsSearchProps = {
  language: SupportedLanguage;
  fromDate?: string | null;
  untilDate?: string | null;
  selectedProject: string;
  selectedEditor: string;
  searchTerm: string;
  projects: Array<{ id: string; name: string }>;
  editors: Array<{ id: string; name: string }>;
};

export type LogsSearchEventNames =
  | "search:from-date-changed"
  | "search:until-date-changed"
  | "search:project-changed"
  | "search:editor-changed"
  | "search:term-changed"
  | "search:clear-all"
  | "search:clear-search-term";

export type LogsSearchEventPayload = {
  fromDate?: string | null;
  untilDate?: string | null;
  selectedProject?: string;
  selectedEditor?: string;
  searchTerm?: string;
};

export const LogsSearch: PimoReactComponent<
  LogsSearchProps,
  LogsSearchEventNames,
  LogsSearchEventPayload
> = ({
  fireEvent,
  fromDate,
  untilDate,
  selectedProject,
  selectedEditor,
  searchTerm,
  projects,
  editors,
  language,
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0" }}>
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <PMButton
            variant="outlined"
            size="medium"
            onClick={() => fireEvent?.("search:clear-all", {})}
            text={
              getLocalizedMessage(language, "logsSearch.clearAllFilters") ||
              "Clear All Filters"
            }
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr 2fr",
            gap: 2,
          }}
        >
          <FormFieldWrapper
            label={getLocalizedMessage(language, "logsSearch.from") || `From`}
          >
            <ControlledDateFormField
              inputFormat={getDateInputFormat()}
              value={fromDate || null}
              onChange={(newValue: Dayjs | null) =>
                fireEvent?.("search:from-date-changed", {
                  fromDate: newValue?.toISOString(),
                })
              }
            />
          </FormFieldWrapper>

          <FormFieldWrapper
            label={getLocalizedMessage(language, "logsSearch.until") || `Until`}
          >
            <ControlledDateFormField
              inputFormat={getDateInputFormat()}
              value={untilDate || null}
              onChange={(newValue: Dayjs | null) => {
                return fireEvent?.("search:until-date-changed", {
                  untilDate: newValue?.toISOString(),
                });
              }}
            />
          </FormFieldWrapper>

          <Box>
            <FormFieldWrapper
              label={
                getLocalizedMessage(language, "logsSearch.project") || `Project`
              }
            >
              <ControlledSelectField
                value={selectedProject}
                onChange={(e) => {
                  fireEvent?.("search:project-changed", {
                    selectedProject: e as string,
                  });
                }}
                options={[
                  {
                    value: "",
                    label: getLocalizedMessage(language, "common.all") || "All",
                  },
                  ...projects?.map((project) => ({
                    value: project.id,
                    label: project.name,
                  })),
                ]}
              ></ControlledSelectField>
            </FormFieldWrapper>
          </Box>

          <Box>
            <FormFieldWrapper
              label={
                getLocalizedMessage(language, "logsSearch.editor") || `Editor`
              }
            >
              <ControlledSelectField
                value={selectedEditor}
                onChange={(e) => {
                  fireEvent?.("search:editor-changed", {
                    selectedEditor: e as string,
                  });
                }}
                options={[
                  {
                    value: "",
                    label: getLocalizedMessage(language, "common.all") || "All",
                  },
                  ...editors?.map((editor) => ({
                    value: editor.id,
                    label: editor.name,
                  })),
                ]}
              ></ControlledSelectField>
            </FormFieldWrapper>
          </Box>

          <Box>
            <FormFieldWrapper
              label={
                getLocalizedMessage(language, "logsSearch.search") || `Search`
              }
            >
              <TextField
                value={searchTerm}
                onChange={(e) =>
                  fireEvent?.("search:term-changed", {
                    searchTerm: e.target.value,
                  })
                }
                placeholder={
                  getLocalizedMessage(
                    language,
                    "logsSearch.searchPlaceholder"
                  ) || "Enter search term"
                }
                size="small"
                fullWidth
                sx={{
                  // single-line input
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                  },
                  // multiline textarea
                  "& .MuiInputBase-inputMultiline, & textarea": {
                    fontSize: "14px",
                  },
                  // label size (optional)
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    ...(searchTerm && {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              fireEvent?.("search:clear-search-term", {});
                            }}
                            edge="end"
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }),
                    style: {
                      borderRadius: "6px",
                      height: "45px",
                    },
                  },
                }}
              />
            </FormFieldWrapper>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
